<template>
  <div class="trial-class-picker p-t-m">
    <!-- The strip's header row carries the wrestler name when the picker is
         showing; every other state renders the name itself so multi-wrestler
         pages stay labeled. -->
    <p v-if="wrestlerName && !showingPicker" class="font-semi text-16 m-t-none m-b-xs">{{ wrestlerName }}</p>

    <div v-if="loading" class="text-center p-y-lg text-gray-500">
      <i class="fa-solid fa-spinner fa-spin"></i>
      <span class="m-l-xs">Loading classes…</span>
    </div>

    <div v-else-if="error" class="alert alert-danger">{{ errorMessage }}</div>

    <!-- No exit button here on purpose: with several wrestlers on the page a
         per-card "Done" strands the ones further down. trial-scheduler owns the
         single footer CTA and listens for the booked event. -->
    <div v-else-if="booked" class="flex align-items-center gap-x-sm p-y-sm" data-cy="trial-booked">
      <i class="fa-solid fa-circle-check text-green-600 text-20"></i>
      <div class="flex flex-col align-items-start">
        <span class="font-semi text-14 text-gray-900">{{ wrestlerName ? `${wrestlerName} is scheduled!` : "You're scheduled!" }}</span>
        <span class="text-12 text-gray-600">{{ bookedSummary }}</span>
      </div>
    </div>

    <div v-else-if="events.length === 0" class="text-center p-y-lg text-gray-500">
      <p class="m-b-xxs">No upcoming classes are available to book yet.</p>
      <!-- Coaches get the real reason: eligibility is "future practices on the
           rosters this trial's session syncs to", so an empty list is usually a
           calendar gap, not a setup delay. Families can't act on that, so they
           keep the simpler wait-and-retry copy. -->
      <p v-if="coachView" class="text-12 m-b-md">
        The trial may still be setting up — try again in a moment. If it stays empty, there may be no
        upcoming practices on the calendar for the rosters tied to this trial pass.
      </p>
      <p v-else class="text-12 m-b-md">Your trial may still be setting up — try again in a moment.</p>
      <button type="button" class="btn btn-default" @click="fetchEvents">Refresh</button>
    </div>

    <div v-else>
      <!-- The wrestler's name doubles as the card heading, sharing a row with
           the month + Today controls instead of stacking a sparse row above. -->
      <!-- The whole booking window arrives in one fetch, so there is nothing
           for "More" to load — it would only append dimmed days. -->
      <date-strip-picker
        v-model="selectedDate"
        :active-dates="activeDates"
        :title="wrestlerName"
        :show-more="false"
      ></date-strip-picker>

      <p class="font-semi text-14 text-gray-900 m-b-xs">{{ selectedDate.format('dddd, MMMM D, YYYY') }}</p>

      <div v-if="selectedDateEvents.length === 0">
        <div class="flex p-md gap-sm border border-solid border-gray-200 rounded-m align-items-baseline m-b-xs">
          <i class="fa-solid fa-circle-exclamation text-red-700"></i>
          <p class="font-semi m-y-none">No classes on {{ selectedDate.format('M/D') }}</p>
        </div>
        <button
          v-if="nextActiveDate"
          type="button"
          class="btn btn-default btn-outline w-full flex justify-center align-items-center gap-sm"
          @click="goToNextActiveDate"
        >
          Next available class <i class="fa-solid fa-chevron-right"></i>
        </button>
      </div>

      <div v-else class="flex flex-col gap-y-xs">
        <div
          v-for="event in selectedDateEvents"
          :key="event.id"
          class="rounded-m border border-solid"
          :class="selectedEventId === event.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'"
        >
          <div class="flex align-items-center">
            <button
              type="button"
              class="flex-1 flex align-items-center justify-space-between p-sm border-none cursor-pointer text-left"
              style="background: transparent;"
              data-cy="trial-class-option"
              @click="selectedEventId = event.id"
            >
              <span class="flex align-items-center gap-x-sm">
                <span :style="dotStyle(event)"></span>
                <span class="flex flex-col align-items-start">
                  <span class="text-14 font-semi text-gray-900">{{ event.name }}</span>
                  <span class="text-12 text-gray-500">
                    {{ timeRange(event) }}<template v-if="event.location"> · {{ event.location }}</template>
                  </span>
                </span>
              </span>
              <i v-if="selectedEventId === event.id" class="fa-solid fa-circle-check text-blue-600"></i>
            </button>
            <!-- Rendered only when there is actually something to expand, so
                 rows without notes stay clean. -->
            <button
              v-if="event.notes"
              type="button"
              class="p-sm border-none cursor-pointer text-gray-500 hover:text-gray-700"
              style="background: transparent;"
              :aria-expanded="isExpanded(event.id) ? 'true' : 'false'"
              :aria-label="isExpanded(event.id) ? 'Hide class details' : 'Show class details'"
              @click="toggleDetails(event.id)"
            >
              <i :class="isExpanded(event.id) ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
            </button>
          </div>
          <div
            v-if="isExpanded(event.id)"
            class="p-sm border-t border-solid border-gray-200 text-12 text-gray-600"
          >
            <p v-if="event.location" class="m-y-none">
              <i class="fa-solid fa-location-dot m-r-xxs"></i>{{ event.location }}
            </p>
            <!-- Trix HTML, sanitized server-side (sanitize_trix) before it
                 reaches this payload. -->
            <div :class="event.location ? 'm-t-xs' : ''" v-html="event.notes"></div>
          </div>
        </div>
      </div>

      <div class="m-t-md">
        <!-- Shown only while the chosen class is off-screen (user browsed to
             another day) — otherwise the highlighted row is the summary. -->
        <template v-if="selectedEventOffscreen">
          <p class="font-semi text-14 text-gray-900 m-t-md m-b-xs">Currently selected</p>
          <div class="flex align-items-center gap-x-sm bg-gray-50 border border-solid border-gray-200 rounded-m p-sm m-b-sm">
            <i class="fa-solid fa-circle-check text-blue-600"></i>
            <div class="flex flex-col align-items-start">
              <span class="text-14 font-semi text-gray-900">{{ selectedEvent.name }}</span>
              <span class="text-12 text-gray-600">{{ selectedDayLabel(selectedEvent) }}</span>
            </div>
          </div>
        </template>
        <!-- Booking failures render here rather than through `error`, which
             swaps out the whole picker — a failed POST must keep the chosen
             class and the button on screen so the user can just retry. -->
        <div v-if="bookingError" class="alert alert-danger m-b-sm">{{ bookingErrorMessage }}</div>
        <button
          type="button"
          class="btn btn-primary w-full"
          :disabled="!selectedEventId || booking"
          data-cy="confirm-trial-booking"
          @click="confirmBooking"
        >
          <i v-if="booking" class="fa-solid fa-spinner fa-spin m-r-xxs"></i>
          Schedule this class
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import DateStripPicker from './date_strip_picker.vue';

export default {
  name: 'trial-class-picker',
  components: { DateStripPicker },
  props: {
    wrestlerId: { type: [Number, String], required: true },
    wrestlerName: { type: String, default: '' },
    // Base path for the trial bookings API. Override only for testing.
    bookingsBaseUrl: { type: String, default: '/api/v1/trial_bookings' },
    // True where a coach is booking or rescheduling on a family's behalf (the
    // prospect drawer). Only affects the empty-state copy — the family-facing
    // scheduler leaves this off.
    coachView: { type: Boolean, default: false },
  },
  data() {
    return {
      events: [],
      loading: true,
      error: false,
      errorMessage: '',
      selectedDate: moment(),
      selectedEventId: null,
      expandedEventIds: [],
      booking: false,
      bookingError: false,
      bookingErrorMessage: '',
      booked: false,
      bookedEvent: null,
    };
  },
  computed: {
    // 'YYYY-MM-DD' for each day that has at least one eligible class — drives
    // the strip's dimming.
    activeDates() {
      const seen = new Set();
      const dates = [];
      this.events.forEach((event) => {
        const key = moment(event.start_at).format('YYYY-MM-DD');
        if (!seen.has(key)) {
          seen.add(key);
          dates.push(key);
        }
      });
      return dates;
    },
    selectedDateEvents() {
      return this.events.filter((event) => moment(event.start_at).isSame(this.selectedDate, 'day'));
    },
    selectedEvent() {
      return this.events.find((e) => e.id === this.selectedEventId) || null;
    },
    showingPicker() {
      return !this.loading && !this.error && !this.booked && this.events.length > 0;
    },
    // True when a class is chosen but its row isn't in the day currently on
    // screen — the only time the footer needs its own selection summary.
    selectedEventOffscreen() {
      return !!this.selectedEvent
        && !moment(this.selectedEvent.start_at).isSame(this.selectedDate, 'day');
    },
    // First day with a class after the selected day; wraps back to the first
    // day with a class so the button always leads somewhere.
    nextActiveDate() {
      if (this.activeDates.length === 0) return null;
      const after = this.activeDates.find((d) => moment(d, 'YYYY-MM-DD').isAfter(this.selectedDate, 'day'));
      return moment(after || this.activeDates[0], 'YYYY-MM-DD');
    },
    bookedSummary() {
      if (!this.bookedEvent) return '';
      return `${this.bookedEvent.name} · ${this.selectedDayLabel(this.bookedEvent)}`;
    },
  },
  mounted() {
    this.fetchEvents();
  },
  methods: {
    fetchEvents() {
      this.loading = true;
      this.error = false;
      axios
        .get(`${this.bookingsBaseUrl}/eligible_events`, { params: { wrestler_id: this.wrestlerId } })
        .then((response) => {
          this.events = (response.data && response.data.events) || [];
          // Land on the first day that actually has a class — most clubs only
          // run a trial-eligible class a couple days a week, so defaulting to
          // today would usually greet the user with an empty day.
          if (this.events.length > 0) {
            this.selectedDate = moment(this.events[0].start_at);
          }
          this.loading = false;
        })
        .catch((err) => {
          this.loading = false;
          this.error = true;
          this.errorMessage = this.getErrorMessage(err);
        });
    },
    confirmBooking() {
      if (!this.selectedEventId) return;
      this.booking = true;
      this.bookingError = false;
      axios
        .post(this.bookingsBaseUrl, { wrestler_id: this.wrestlerId, event_id: this.selectedEventId })
        .then((response) => {
          this.bookedEvent = this.selectedEvent;
          this.booked = true;
          this.booking = false;
          // The POST returns the updated prospect, so hosts that show booking
          // state of their own (the prospect drawer) can repaint from this
          // rather than refetching. trial-scheduler ignores the payload.
          this.$emit('booked', { wrestlerId: this.wrestlerId, prospect: response.data });
        })
        .catch((err) => {
          this.booking = false;
          this.bookingError = true;
          this.bookingErrorMessage = this.getErrorMessage(err);
        });
    },
    goToNextActiveDate() {
      if (this.nextActiveDate) this.selectedDate = this.nextActiveDate;
    },
    isExpanded(eventId) {
      return this.expandedEventIds.includes(eventId);
    },
    toggleDetails(eventId) {
      if (this.isExpanded(eventId)) {
        this.expandedEventIds = this.expandedEventIds.filter((id) => id !== eventId);
      } else {
        this.expandedEventIds.push(eventId);
      }
    },
    dotStyle(event) {
      return {
        backgroundColor: event.color || '#9CA3AF',
        width: '10px',
        height: '10px',
        borderRadius: '9999px',
        display: 'inline-block',
        flexShrink: 0,
      };
    },
    // "Mon, Aug 3, 5:30 PM – 6:30 PM" — used where the day isn't already on
    // screen (selected summary, booked confirmation).
    selectedDayLabel(event) {
      return `${moment(event.start_at).format('ddd, MMM D')}, ${this.timeRange(event)}`;
    },
    timeRange(event) {
      let str = moment(event.start_at).format('h:mm A');
      if (event.end_at) str += ` – ${moment(event.end_at).format('h:mm A')}`;
      return str;
    },
    getErrorMessage(err) {
      if (err && err.response && err.response.data && err.response.data.errors) {
        return [].concat(err.response.data.errors).join(' ');
      }
      return 'Something went wrong. Please try again.';
    },
  },
};
</script>
